import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Button,
  FilterChips,
  Input,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  RadioGroup,
  Table,
  TableFooter,
  TCell,
  THead,
  toast,
  Toggle,
  TRow,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultAdminsClient,
  type AdminAccount,
  type AdminPermission,
  type AdminRole,
  type AdminsClient,
  type UpdateAdminPatch,
} from '../admins/admins-client';
import { ROLES, roleLabel, SUPER_ADMIN_ROLE } from '../admins/role-meta';
import { formatRailDate } from '../contests/format-date';

/**
 * The Admins section (API-25, admin-ui.md → Admins): SUPER_ADMIN-only account management. List
 * operator accounts, invite a new one (which reveals a one-time temporary password), and manage a
 * row's role, active state, and forced-reset flag.
 *
 * A role change is data-only — permissions bake into the token at login, so it takes effect at the
 * operator's next sign-in (flows/admin-auth), which the copy says throughout. The last active Super
 * Admin is protected two ways: the manage modal pre-blocks demoting/deactivating it (from the loaded
 * list), and the backend's 409 is surfaced inline as the authoritative backstop when the view is stale.
 */

export interface AdminsScreenProps {
  /** Injected for tests; defaults to the real API-25 client. */
  readonly client?: AdminsClient;
}

const ALL_CHIP = 'all';

export function AdminsScreen({ client = defaultAdminsClient }: AdminsScreenProps) {
  const { getToken, claims } = useAdminAuth();
  const [admins, setAdmins] = useState<AdminAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roleMatrix, setRoleMatrix] = useState<RoleMatrix | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>(ALL_CHIP);
  const [inviting, setInviting] = useState(false);
  const [managing, setManaging] = useState<AdminAccount | null>(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.listAdmins(token);
    if (result.ok) {
      setAdmins(result.admins);
      setError(null);
    } else {
      // An error must not read as "no admins": that would tell an operator the account list is
      // empty when the lookup merely broke.
      setError(result.message ?? 'The admin list failed to load.');
      setAdmins(null);
    }
  }, [client, getToken]);

  // The RBAC reference matrix loads independently of the account list (CAE-483): the last-super-admin
  // pre-block reads the accounts and a compile-time id, never this fetch, so a slow or failed roles
  // read must never hold up managing accounts. A failure shows in place of the card, not empty marks.
  const loadRoles = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.listRoles(token);
    if (result.ok) {
      setRoleMatrix({ roles: result.roles, permissions: result.permissions });
      setRolesError(null);
    } else {
      setRolesError(result.message ?? 'The role permissions failed to load.');
      setRoleMatrix(null);
    }
  }, [client, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  const chips = useMemo(() => {
    const rows = admins ?? [];
    return [
      { key: ALL_CHIP, label: 'All', count: rows.length },
      ...ROLES.map((role) => ({
        key: role.id,
        label: role.label,
        count: rows.filter((a) => a.role === role.id).length,
      })),
    ];
  }, [admins]);

  const visible = useMemo(
    () => (admins ?? []).filter((a) => roleFilter === ALL_CHIP || a.role === roleFilter),
    [admins, roleFilter],
  );

  /** The count of active Super Admins — the manage modal reads it to pre-block removing the last one. */
  const activeSuperAdminCount = useMemo(
    () => (admins ?? []).filter((a) => a.role === SUPER_ADMIN_ROLE && a.isActive).length,
    [admins],
  );

  return (
    <AdminShell
      title="Admins"
      subtitle="Operator accounts and their roles. A role change takes effect at the operator's next sign-in."
      actions={<Button onClick={() => setInviting(true)}>+ Invite admin</Button>}
    >
      <FilterChips chips={chips} active={roleFilter} onSelect={setRoleFilter} className="mt-1" />

      {error !== null && (
        <Banner data-testid="admins-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {admins !== null && visible.length === 0 && (
        <p data-testid="admins-empty" className="mt-8 text-center text-sm text-ink-muted">
          {roleFilter === ALL_CHIP
            ? 'No admin accounts yet.'
            : 'No admin accounts match this filter.'}
        </p>
      )}

      {visible.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          <Table columns="minmax(0,1.6fr) 150px minmax(0,1fr) 150px 110px">
            <THead>
              <TCell>Admin</TCell>
              <TCell>Role</TCell>
              <TCell>Status</TCell>
              <TCell>Last sign-in</TCell>
              <TCell />
            </THead>
            {visible.map((admin) => (
              <TRow key={admin.id} data-testid={`admin-row-${admin.id}`}>
                <TCell variant="primary">
                  <span className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-full bg-accent-soft text-[13px] font-semibold text-accent"
                    >
                      {initials(admin.email)}
                    </span>
                    <span className="truncate font-mono text-[13px] text-ink">{admin.email}</span>
                    {claims?.sub === admin.id && (
                      <span
                        data-testid="admin-you"
                        className="rounded-[4px] border-[0.5px] border-line-strong px-[5px] py-[3px] text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-4"
                      >
                        You
                      </span>
                    )}
                  </span>
                </TCell>
                <TCell>
                  <RolePill role={admin.role} />
                </TCell>
                <TCell>
                  <span className="flex flex-col items-start gap-[3px]">
                    <StatePill isActive={admin.isActive} />
                    {admin.mustResetPw && (
                      <span
                        data-testid="admin-must-reset"
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-warn"
                      >
                        <ShieldIcon />
                        Must reset password
                      </span>
                    )}
                  </span>
                </TCell>
                <TCell variant="mono">
                  {admin.lastLoginAt === null ? 'Never' : formatRailDate(admin.lastLoginAt)}
                </TCell>
                <TCell>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`admin-manage-${admin.id}`}
                    onClick={() => setManaging(admin)}
                  >
                    Manage
                  </Button>
                </TCell>
              </TRow>
            ))}
          </Table>
          <TableFooter>
            <span data-testid="admins-footer">
              Showing <strong>{visible.length}</strong> of <strong>{(admins ?? []).length}</strong>{' '}
              operator accounts
            </span>
          </TableFooter>
        </div>
      )}

      {rolesError !== null && (
        <Banner data-testid="roles-error" variant="danger" className="mt-6">
          {rolesError}
        </Banner>
      )}

      {roleMatrix !== null && <RolePermissionsCard matrix={roleMatrix} />}

      {inviting && (
        <InviteAdminModal
          client={client}
          onClose={() => setInviting(false)}
          onCreated={() => void load()}
        />
      )}

      {managing && (
        <ManageAdminModal
          client={client}
          admin={managing}
          activeSuperAdminCount={activeSuperAdminCount}
          onClose={() => setManaging(null)}
          onUpdated={(updated) => {
            setAdmins((current) =>
              current === null ? current : current.map((a) => (a.id === updated.id ? updated : a)),
            );
            setManaging(null);
          }}
        />
      )}
    </AdminShell>
  );
}

/** Email-derived initials — the account has no name, so the avatar reads from the local part. */
function initials(email: string): string {
  return (email.split('@')[0] ?? email).slice(0, 2).toUpperCase();
}

/** The must-reset shield (admin design ref), matching the flag icon on the list rows. */
function ShieldIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3l7 3v5c0 4.4-2.9 8.1-7 10-4.1-1.9-7-5.6-7-10V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Role pill (design ref `.role-pill`): a bordered tile with a dot; the accent variant marks Super Admin. */
function RolePill({ role }: { role: string }) {
  const isSuper = role === SUPER_ADMIN_ROLE;
  return (
    <span
      className={
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-[6px] border-[0.5px] px-2.5 py-[5px] text-[12px] font-semibold ' +
        (isSuper
          ? 'border-accent/30 bg-accent-soft text-accent'
          : 'border-line-strong bg-surface-muted text-ink-2')
      }
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {roleLabel(role)}
    </span>
  );
}

/** Active/Inactive state (design ref `.adm-state`): a coloured dot and label, no pill fill. */
function StatePill({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={
        'inline-flex items-center gap-[7px] text-[12px] font-medium ' +
        (isActive ? 'text-success' : 'text-ink-4')
      }
    >
      <span className="h-[7px] w-[7px] rounded-full bg-current" aria-hidden="true" />
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

/** The RBAC reference matrix as the roles endpoint serves it (CAE-483). */
interface RoleMatrix {
  readonly roles: readonly AdminRole[];
  readonly permissions: readonly AdminPermission[];
}

/**
 * The seeded RBAC matrix as a reference card (design ref → "Role permissions"): a read-only grid of
 * permission × role, so an operator assigning a role can see exactly what it grants. The grants come
 * from the roles endpoint, which reads the same DB tables login mints token permissions from — so the
 * card can't drift from what a role actually grants. Columns render in the local role order (labels
 * and privilege ordering live in role-meta); the ✓ / — marks are the API's grants, keyed by role id.
 */
function RolePermissionsCard({ matrix }: { matrix: RoleMatrix }) {
  const grantsByRole = new Map(matrix.roles.map((role) => [role.id, new Set(role.permissionIds)]));
  return (
    <section className="mt-6 overflow-hidden rounded-tile border-[0.5px] border-line bg-surface">
      <header className="border-b-[0.5px] border-line px-5 py-4">
        <h2 className="text-[15px] font-semibold text-ink">Role permissions</h2>
        <p className="mt-0.5 text-[12px] text-ink-3">
          The seeded RBAC matrix — permissions are baked into the token at sign-in.
        </p>
      </header>
      <div
        role="table"
        aria-label="Role permissions"
        className="grid text-[13px]"
        style={{ gridTemplateColumns: '200px repeat(4, minmax(0,1fr))' }}
      >
        <div className="border-b-[0.5px] border-line bg-surface-muted px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-3">
          Permission
        </div>
        {ROLES.map((role) => (
          <div
            key={role.id}
            className="border-b-[0.5px] border-line bg-surface-muted px-3.5 py-2.5 text-center text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-3"
          >
            {role.label}
          </div>
        ))}
        {matrix.permissions.map((permission, rowIndex) => {
          const last = rowIndex === matrix.permissions.length - 1;
          const cellBorder = last ? '' : 'border-b-[0.5px] border-line';
          return [
            <div
              key={`${permission.id}-name`}
              className={`px-3.5 py-2.5 font-mono text-[12px] text-ink-2 ${cellBorder}`}
            >
              {permission.id}
            </div>,
            ...ROLES.map((role) => {
              const granted = grantsByRole.get(role.id)?.has(permission.id) ?? false;
              return (
                <div
                  key={`${permission.id}-${role.id}`}
                  className={`px-3.5 py-2.5 text-center ${cellBorder} ${
                    granted ? 'text-success' : 'text-line-strong'
                  }`}
                >
                  {granted ? '✓' : '—'}
                </div>
              );
            }),
          ];
        })}
      </div>
    </section>
  );
}

const ROLE_OPTIONS = ROLES.map((role) => ({
  value: role.id,
  label: role.label,
  description: role.description,
}));

interface InviteAdminModalProps {
  readonly client: AdminsClient;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

/**
 * Invite an admin (API-25 POST). Two steps: the form (email + role), then the reveal of the one-time
 * temporary password — the only time it is ever shown, so the operator must copy it out of band here
 * (the backend stores only its hash). Done refreshes the list so the new account appears.
 */
function InviteAdminModal({ client, onClose, onCreated }: InviteAdminModalProps) {
  const { getToken } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('GAME_MANAGER');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; temporaryPassword: string } | null>(null);

  const canSubmit = email.trim() !== '';

  async function submit() {
    const token = getToken();
    if (token === null || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    const result = await client.createAdmin(token, { email: email.trim(), role });
    setSubmitting(false);
    if (result.ok) {
      setCreated({ email: result.admin.email, temporaryPassword: result.admin.temporaryPassword });
      return;
    }
    setError(result.message ?? `Could not create this admin (${result.code ?? result.status}).`);
  }

  async function copyPassword() {
    if (created === null) return;
    await navigator.clipboard?.writeText(created.temporaryPassword);
    toast('Temporary password copied');
  }

  if (created !== null) {
    return (
      <Modal open onClose={onClose} maxWidth={520}>
        <ModalHead
          context="Access management"
          title="Admin invited"
          subtitle="Share this one-time password with the operator over a secure channel."
          onClose={onClose}
        />
        <ModalBody>
          <div className="flex flex-col gap-4 px-7 py-[22px]">
            <p className="text-[13.5px] text-ink-2">
              Account created for{' '}
              <span className="font-mono font-semibold text-ink">{created.email}</span>
            </p>
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium text-ink-3">Temporary password</span>
              <div className="flex items-center gap-2.5 rounded-tile border border-line bg-surface-muted px-3.5 py-2.5">
                <span
                  data-testid="temp-password"
                  className="flex-1 font-mono text-[15px] tracking-wide text-ink"
                >
                  {created.temporaryPassword}
                </span>
                <Button variant="outline" size="sm" onClick={() => void copyPassword()}>
                  Copy
                </Button>
              </div>
            </div>
            <Banner variant="warn">
              This password is shown only once and cannot be retrieved later. Copy it now and share
              it with the operator over a secure channel. If it&apos;s lost, force a password reset
              from Manage.
            </Banner>
          </div>
        </ModalBody>
        <ModalFoot
          summary={
            <>
              Operator <strong className="font-semibold text-ink">must reset</strong> at first
              sign-in
            </>
          }
        >
          <Button
            onClick={() => {
              onCreated();
              onClose();
            }}
          >
            Done
          </Button>
        </ModalFoot>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} maxWidth={520}>
      <ModalHead
        context="Access management"
        title="Invite an admin"
        subtitle="Creates an account and generates a one-time temporary password shown only now. The operator must reset it at first sign-in."
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-4 px-7 py-[22px]">
          <Input
            label="Email"
            type="email"
            required
            placeholder="operator@caesars.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <RadioGroup label="Role" options={ROLE_OPTIONS} value={role} onChange={setRole} />
          {error !== null && <Banner variant="danger">{error}</Banner>}
        </div>
      </ModalBody>
      <ModalFoot
        summary={
          <>
            Starts with <strong className="font-semibold text-ink">Must reset password</strong>
          </>
        }
      >
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit || submitting} onClick={() => void submit()}>
          Invite admin
        </Button>
      </ModalFoot>
    </Modal>
  );
}

interface ManageAdminModalProps {
  readonly client: AdminsClient;
  readonly admin: AdminAccount;
  readonly activeSuperAdminCount: number;
  readonly onClose: () => void;
  readonly onUpdated: (admin: AdminAccount) => void;
}

/**
 * Manage an admin (API-25 PUT): role, active state, forced reset. The last active Super Admin is
 * pre-blocked from demote/deactivate here (Save disabled + a clear reason) so the operator learns
 * before acting; the backend 409 is still surfaced inline should the loaded view be stale.
 */
function ManageAdminModal({
  client,
  admin,
  activeSuperAdminCount,
  onClose,
  onUpdated,
}: ManageAdminModalProps) {
  const { getToken } = useAdminAuth();
  const [role, setRole] = useState(admin.role);
  const [isActive, setIsActive] = useState(admin.isActive);
  const [forceReset, setForceReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = role !== admin.role || isActive !== admin.isActive || forceReset;

  // The last-active-Super-Admin guard, evaluated from the loaded list: this row is currently an
  // active Super Admin, the draft stops it being one, and no other active Super Admin remains.
  const wasActiveSuper = admin.role === SUPER_ADMIN_ROLE && admin.isActive;
  const staysActiveSuper = role === SUPER_ADMIN_ROLE && isActive;
  const blocked = wasActiveSuper && !staysActiveSuper && activeSuperAdminCount <= 1;

  async function submit() {
    const token = getToken();
    if (token === null || !changed || blocked) return;
    const patch: UpdateAdminPatch = {};
    if (role !== admin.role) patch.role = role;
    if (isActive !== admin.isActive) patch.isActive = isActive;
    if (forceReset) patch.forceReset = true;

    setSubmitting(true);
    setError(null);
    const result = await client.updateAdmin(token, admin.id, patch);
    setSubmitting(false);
    if (result.ok) {
      toast('Admin updated');
      onUpdated(result.admin);
      return;
    }
    // A 409 means the loaded view was stale (another operator changed a Super Admin). Say so and
    // keep the modal open — nothing was lost, the operator just needs the current picture.
    setError(result.message ?? `Could not update this admin (${result.code ?? result.status}).`);
  }

  return (
    <Modal open onClose={onClose} maxWidth={520}>
      <ModalHead
        context="Access management"
        title={admin.email}
        subtitle="Role, activation, and password reset. Changes apply at the operator's next sign-in."
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-4 px-7 py-[22px]">
          <RadioGroup label="Role" options={ROLE_OPTIONS} value={role} onChange={setRole} />

          <div className="flex items-center justify-between gap-4">
            <span className="flex flex-col">
              <span className="text-[13px] font-medium text-ink">Active</span>
              <span className="text-[12px] text-ink-3">Inactive accounts cannot sign in.</span>
            </span>
            <Toggle aria-label="Active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className="flex flex-col">
              <span className="text-[13px] font-medium text-ink">Force password reset</span>
              <span className="text-[12px] text-ink-3">
                Require a new password at next sign-in.
              </span>
            </span>
            <Toggle
              aria-label="Force password reset"
              checked={forceReset}
              onCheckedChange={setForceReset}
            />
          </div>

          {blocked && (
            <Banner data-testid="admin-guard" variant="danger">
              This is the last active Super Admin. Assign another Super Admin before demoting or
              deactivating this account.
            </Banner>
          )}
          {error !== null && (
            <Banner data-testid="admin-manage-error" variant="danger">
              {error}
            </Banner>
          )}
        </div>
      </ModalBody>
      <ModalFoot summary={blocked ? 'Blocked — last Super Admin' : 'Applies at next sign-in'}>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!changed || blocked || submitting} onClick={() => void submit()}>
          Save changes
        </Button>
      </ModalFoot>
    </Modal>
  );
}
